import { getAxiosInstance } from '../../react/utils/networkUtil';
import { PaginationResult } from '../models/AppPagination';
import {
  SpatialData_Info,
  SpatialData_Feature,
  SpatialData_FeatureCollection,
} from '../models/SpatialData';

// ==========================================
// 1. Get Spatial Data Info
// ==========================================

export type SpatialData_GetInfo_Response = SpatialData_Info;

export const remote_spatialData_getInfo = async (
  mapDataId: number | string
): Promise<SpatialData_GetInfo_Response> => {
  try {
    const response = await getAxiosInstance().get(`/api/spatial-data/${mapDataId}/info`);
    return response.data;
  } catch (e) {
    console.error('ERROR: when remote_spatialData_getInfo', e);
    throw e;
  }
};

// ==========================================
// 2. Get Features (Viewport Bounding Box Query)
// ==========================================

export type SpatialData_GetFeatures_RequestQuery = {
  bbox?: string; // "minX,minY,maxX,maxY"
  limit?: number;
  pageSize?: number;
};

export type SpatialData_GetFeatures_Response = SpatialData_FeatureCollection;

// Alias for backwards compatibility
export type SpatialData_Features_RequestQuery = SpatialData_GetFeatures_RequestQuery;

export const remote_spatialData_getFeatures = async (
  mapDataId: number | string,
  query?: SpatialData_GetFeatures_RequestQuery
): Promise<SpatialData_GetFeatures_Response> => {
  try {
    const response = await getAxiosInstance().get(`/api/spatial-data/${mapDataId}/features`, {
      params: query,
    });
    return response.data;
  } catch (e) {
    console.error('ERROR: when remote_spatialData_getFeatures', e);
    throw e;
  }
};

// ==========================================
// 3. Get Attribute Table (Full-Text Search & Pagination)
// ==========================================

export type SpatialData_GetAttributes_RequestQuery = {
  page?: number;
  pageSize?: number;
  limit?: number;
  search?: string;
  q?: string;
  bbox?: string;
};

export type SpatialData_GetAttributes_Response<T = Record<string, any>> = PaginationResult<T> & {
  mapDataId?: number | string;
  data?: T[];
  page?: number;
  pageSize?: number;
  limit?: number;
  total?: number;
  pageCount?: number;
};

// Aliases for backwards compatibility
export type SpatialData_Attributes_RequestQuery = SpatialData_GetAttributes_RequestQuery;
export type SpatialData_Attributes_Response<T = Record<string, any>> = SpatialData_GetAttributes_Response<T>;

export const remote_spatialData_getAttributes = async (
  mapDataId: number | string,
  query?: SpatialData_GetAttributes_RequestQuery
): Promise<SpatialData_GetAttributes_Response> => {
  try {
    const response = await getAxiosInstance().get(`/api/spatial-data/${mapDataId}/attributes`, {
      params: query,
    });
    return response.data;
  } catch (e) {
    console.error('ERROR: when remote_spatialData_getAttributes', e);
    throw e;
  }
};

// ==========================================
// 4. Get Feature Detail by ID
// ==========================================

export type SpatialData_GetFeatureDetail_Response = SpatialData_Feature;

export const remote_spatialData_getFeatureDetail = async (
  mapDataId: number | string,
  featureId: number | string
): Promise<SpatialData_GetFeatureDetail_Response> => {
  try {
    const response = await getAxiosInstance().get(`/api/spatial-data/${mapDataId}/features/${featureId}`);
    return response.data;
  } catch (e) {
    console.error('ERROR: when remote_spatialData_getFeatureDetail', e);
    throw e;
  }
};

// ==========================================
// 5. Update Feature Properties (Transactional Update)
// ==========================================

export type SpatialData_UpdateFeature_RequestBody = {
  [key: string]: any;
  map_data_id?: number | string;
};

export type SpatialData_UpdateFeature_Response = {
  success: boolean;
  mapDataId: number | string;
  documentId?: string;
  updatedId: number | string;
  properties: Record<string, any>;
};

// Alias for backwards compatibility
export type SpatialData_UpdateFeature_Request = SpatialData_UpdateFeature_RequestBody;

export const remote_spatialData_updateFeature = async (
  mapDataId: number | string,
  featureId: number | string,
  data: SpatialData_UpdateFeature_RequestBody
): Promise<SpatialData_UpdateFeature_Response> => {
  try {
    const response = await getAxiosInstance().patch(
      `/api/spatial-data/${mapDataId}/features/${featureId}`,
      data
    );
    return response.data;
  } catch (e) {
    console.error('ERROR: when remote_spatialData_updateFeature', e);
    throw e;
  }
};

export const SpatialDataAPIs = {
  remote_spatialData_getInfo,
  remote_spatialData_getFeatures,
  remote_spatialData_getAttributes,
  remote_spatialData_getFeatureDetail,
  remote_spatialData_updateFeature,
};
